/**
 * The content browser: search what the assistant knows, and hide what it
 * should not answer from.
 *
 * Exclusion is the only destructive-feeling action a customer takes here, so
 * it is reversible, visible, and explained: the "Hidden" filter is a first-
 * class view rather than a checkbox, because there is no way to un-hide
 * something you cannot find.
 */

import { useState } from "react";

import { useDocument, useDocuments, useSetExclusion } from "../../api/queries";
import type { DocumentSummary } from "../../api/types";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Input,
  Loading,
  Modal,
  Select,
} from "../../ui/primitives";
import { useSiteContext } from "./SiteLayout";

type View = "all" | "hidden";

export function ContentPage() {
  const { orgId, siteId } = useSiteContext();

  const [search, setSearch] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [view, setView] = useState<View>("all");
  const [inspecting, setInspecting] = useState<string | null>(null);

  const documents = useDocuments(orgId, siteId, {
    search: search || undefined,
    source_type: sourceType || undefined,
    excluded: view === "hidden" ? true : undefined,
  });
  const setExclusion = useSetExclusion(orgId, siteId);

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <label htmlFor="q" className="mb-1 block text-sm font-medium">
              Search titles
            </label>
            <Input
              id="q"
              type="search"
              placeholder="Espresso machine"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div>
            <label htmlFor="type" className="mb-1 block text-sm font-medium">
              Type
            </label>
            <Select
              id="type"
              value={sourceType}
              onChange={(event) => setSourceType(event.target.value)}
            >
              <option value="">All</option>
              <option value="product">Products</option>
              <option value="page">Pages</option>
              <option value="post">Posts</option>
            </Select>
          </div>

          <div>
            <label htmlFor="view" className="mb-1 block text-sm font-medium">
              Show
            </label>
            <Select
              id="view"
              value={view}
              onChange={(event) => setView(event.target.value as View)}
            >
              <option value="all">Everything</option>
              <option value="hidden">Hidden only</option>
            </Select>
          </div>
        </div>
      </Card>

      {setExclusion.error != null && <ErrorNote error={setExclusion.error} />}

      {documents.isLoading ? (
        <Loading />
      ) : documents.error ? (
        <ErrorNote error={documents.error} />
      ) : (documents.data ?? []).length === 0 ? (
        <Empty title={view === "hidden" ? "Nothing is hidden" : "No content found"}>
          {view === "hidden"
            ? "Everything in your catalogue is available to the assistant."
            : "Run a sync from the Overview tab to pull in your products and pages."}
        </Empty>
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {(documents.data ?? []).map((document) => (
              <li key={document.id}>
                <Row
                  document={document}
                  onInspect={() => setInspecting(document.id)}
                  onToggle={(excluded) =>
                    setExclusion.mutate({ documentId: document.id, excluded })
                  }
                  busy={setExclusion.isPending}
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <InspectDialog
        documentId={inspecting}
        onClose={() => setInspecting(null)}
        orgId={orgId}
        siteId={siteId}
      />
    </div>
  );
}

function Row({
  document,
  onInspect,
  onToggle,
  busy,
}: {
  document: DocumentSummary;
  onInspect: () => void;
  onToggle: (excluded: boolean) => void;
  busy: boolean;
}) {
  const hidden = document.excluded_at !== null;

  return (
    <div className="flex flex-wrap items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{document.title}</span>
          <Badge>{document.source_type}</Badge>
          {hidden && <Badge tone="warn">Hidden</Badge>}
        </div>
        {document.url && <div className="truncate text-xs text-muted">{document.url}</div>}
      </div>

      <Button size="sm" variant="ghost" onClick={onInspect}>
        Inspect
      </Button>
      <Button
        size="sm"
        variant={hidden ? "secondary" : "danger"}
        onClick={() => onToggle(!hidden)}
        disabled={busy}
      >
        {hidden ? "Show again" : "Hide"}
      </Button>
    </div>
  );
}

/**
 * What the assistant actually reads.
 *
 * The point of this dialog is that the text here is the text retrieval sees —
 * so when an answer is wrong, the customer can tell the difference between
 * "the assistant misread the page" and "the page says the wrong thing".
 */
function InspectDialog({
  documentId,
  onClose,
  orgId,
  siteId,
}: {
  documentId: string | null;
  onClose: () => void;
  orgId: string;
  siteId: string;
}) {
  const document = useDocument(orgId, siteId, documentId);

  return (
    <Modal open={documentId !== null} onClose={onClose} title="Indexed content">
      {document.isLoading ? (
        <Loading />
      ) : document.error ? (
        <ErrorNote error={document.error} />
      ) : document.data ? (
        <div className="space-y-3">
          <div>
            <h3 className="font-medium">{document.data.title}</h3>
            <p className="text-xs text-muted">
              {document.data.chunk_count} searchable chunk
              {document.data.chunk_count === 1 ? "" : "s"}
              {document.data.excluded_at && " · hidden from the assistant"}
            </p>
          </div>

          {document.data.product && (
            <dl className="grid grid-cols-2 gap-2 rounded-lg bg-surface p-3 text-sm">
              <dt className="text-muted">SKU</dt>
              <dd>{document.data.product.sku ?? "—"}</dd>
              <dt className="text-muted">Price</dt>
              <dd>
                {document.data.product.price
                  ? `${document.data.product.price} ${document.data.product.currency ?? ""}`
                  : "—"}
              </dd>
              <dt className="text-muted">Stock</dt>
              <dd>{document.data.product.stock_status.replace("_", " ")}</dd>
            </dl>
          )}

          <pre className="max-h-80 overflow-auto rounded-lg bg-surface p-3 text-xs whitespace-pre-wrap">
            {document.data.content}
          </pre>

          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
